/**
 * Shared reading of a `masterPricing` row (the `/map-pricing` list) so the MAP
 * Pricing Data page and Price History count the same rebates.
 */
export interface MapPricingRowLike {
  id: string;
  model: string;
  brand: string;
  region?: 'US' | 'CA' | string;
  instantRebate?: number;
  tradeCredit?: number;
  effectiveDate?: Date | string | null;
  endDate?: Date | string | null;
  status?: string;
}

export type LiveRowStatus = 'active' | 'upcoming' | 'expired' | string;

/** True when the row carries an instant rebate or a trade credit. */
export function hasRebate(row: Pick<MapPricingRowLike, 'instantRebate' | 'tradeCredit'>): boolean {
  return (Number(row.instantRebate) || 0) > 0 || (Number(row.tradeCredit) || 0) > 0;
}

/** A row's status at `now`, from its own window; the stored status only when it names no start. */
export function liveRowStatus(record: Pick<MapPricingRowLike, 'effectiveDate' | 'endDate' | 'status'>, now: number = Date.now()): LiveRowStatus {
  const start = record.effectiveDate ? new Date(record.effectiveDate).getTime() : NaN;
  const end = record.endDate ? new Date(record.endDate).getTime() : NaN;
  if (Number.isFinite(start) && start > now) return 'upcoming';
  if (Number.isFinite(end) && end < now) return 'expired';
  if (Number.isFinite(start)) return 'active';
  return record.status || 'active';
}

export interface PricingRebateRecord {
  id: string;
  model: string;
  brand: string;
  region: 'US' | 'CA';
  rebateAmount: number;
  rebateType: 'instant' | 'trade_credit';
  effectiveDate: Date;
  endDate?: Date;
  status: 'active' | 'upcoming' | 'expired';
}

/** One entry per pricing row that carries an instant rebate or trade credit. */
export function rebateRecordsFromPricing(rows: MapPricingRowLike[], now: number = Date.now()): PricingRebateRecord[] {
  const out: PricingRebateRecord[] = [];
  for (const row of rows) {
    if (!hasRebate(row)) continue;
    const instant = Number(row.instantRebate) || 0;
    const trade = Number(row.tradeCredit) || 0;
    const status = liveRowStatus(row, now);
    out.push({
      id: row.id,
      model: row.model,
      brand: row.brand,
      region: row.region === 'CA' ? 'CA' : 'US',
      rebateAmount: instant > 0 ? instant : trade,
      rebateType: instant > 0 ? 'instant' : 'trade_credit',
      effectiveDate: row.effectiveDate ? new Date(row.effectiveDate) : new Date(now),
      endDate: row.endDate ? new Date(row.endDate) : undefined,
      status: status === 'upcoming' || status === 'expired' ? status : 'active',
    });
  }
  return out;
}
